import { useEffect, useState } from 'react';
import catalog from '../../../../../assets/travel-icons/catalog.json';

/**
 * The icons behind the homepage's "Most popular destinations" cards: a Fluent emoji per
 * link and a one-colour glyph per card header. The files are generated by the website's
 * scripts/travel-icons.mjs, which writes the same list into both apps.
 *
 * Whatever a card leaves on Automatic, the website fills in by the rules below, so they
 * mirror src/utils/travelIcons.js and PopularDest.jsx there. Keep the two in step, or the
 * preview here stops matching the page.
 */

// In the order a card gets them when neither the dashboard nor its title picks one.
export const TONES = ['blue', 'orange', 'pink', 'green', 'purple', 'yellow', 'teal'];

// The website's card tones (PopularDest.module.css): tile background and glyph colour.
export const TONE_STYLES = {
  blue: { background: '#E8F1FC', color: '#3D91E0' },
  orange: { background: '#FEF3E4', color: '#F39C3B' },
  pink: { background: '#FCE4EA', color: '#E94C6B' },
  green: { background: '#DCF7EC', color: '#22AD80' },
  purple: { background: '#EEE9FE', color: '#7C5CE0' },
  yellow: { background: '#FFF6D6', color: '#DDA400' },
  teal: { background: '#DDF4F6', color: '#0E9AA7' },
};

// The little scene behind each emoji, as the website paints it.
export const TILE_BACKGROUNDS = {
  sky: 'linear-gradient(180deg, #A9DAFA 0%, #E1F3FD 100%)',
  sunset: 'linear-gradient(180deg, #FFA95E 0%, #FFDA7A 100%)',
  beach: 'linear-gradient(180deg, #A9DAFA 0%, #DDF2FD 58%, #F8DD9F 58%, #F2CD84 100%)',
  sea: 'linear-gradient(180deg, #A6DDFB 0%, #74D2D4 100%)',
  sand: 'linear-gradient(180deg, #FFEAC4 0%, #F7D197 100%)',
  mint: 'linear-gradient(180deg, #D3F3E0 0%, #A6E0BE 100%)',
  rose: 'linear-gradient(180deg, #FFDDE6 0%, #FFBFD0 100%)',
  lavender: 'linear-gradient(180deg, #E4DDFF 0%, #CBBDFC 100%)',
};

export const EMOJI_LIST = catalog.emoji;
export const GLYPH_LIST = catalog.glyphs;

const emojiInfo = new Map(catalog.emoji.map((e) => [e.name, e]));
const glyphNames = new Set(catalog.glyphs.map((g) => g.name));

export const isEmoji = (name) => emojiInfo.has(name);
export const isGlyph = (name) => glyphNames.has(name);
export const emojiLabel = (name) => emojiInfo.get(name)?.label || name;
export const glyphLabel = (name) => catalog.glyphs.find((g) => g.name === name)?.label || name;
export const emojiTileBackground = (name) =>
  TILE_BACKGROUNDS[emojiInfo.get(name)?.tile] || TILE_BACKGROUNDS.sky;

// Whole words only: the resort "Side" must not match "Seaside", nor "car" match "Caribbean".
const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const hasWord = (text, word) =>
  new RegExp(`(^|[^\\p{L}\\p{N}])${escape(word)}(?=$|[^\\p{L}\\p{N}])`, 'iu').test(text);

/** The emoji for a link left on Automatic, from its label and place. */
export const inferLinkIcon = (...texts) => {
  const text = texts.filter(Boolean).join(' ');
  if (!text) return null;
  const rule = catalog.destinationRules.find(([words]) => words.some((w) => hasWord(text, w)));
  return rule ? rule[1] : null;
};

/** A card's glyph and tone when left on Automatic, from its title. */
export const inferGroupStyle = (title) => {
  const text = String(title || '');
  const rule = catalog.groupRules.find(([words]) => words.some((w) => hasWord(text, w)));
  return rule ? { glyph: rule[1], tone: rule[2] } : null;
};

/** What the website shows for a card header: the chosen glyph and tone, or the automatic ones. */
export const resolveGroupStyle = (group, index) => {
  const auto = inferGroupStyle(group?.title) || {};
  return {
    autoGlyph: auto.glyph || 'map-marker',
    autoTone: auto.tone || TONES[index % TONES.length],
    glyph: isGlyph(group?.icon) ? group.icon : auto.glyph || 'map-marker',
    tone: TONES.includes(group?.tone) ? group.tone : auto.tone || TONES[index % TONES.length],
  };
};

/** The emoji the website picks for a link left on Automatic. */
export const autoLinkIcon = (link, groupTitle) =>
  inferLinkIcon(link?.label, link?.dest?.name, link?.dest?.countryName) ||
  inferLinkIcon(groupTitle) ||
  'world-map';

/** The label under a card when "View all" has no text of its own. */
export const defaultViewAllLabel = (title) => `View all ${String(title || '').trim().toLowerCase()}`.trim();

/**
 * Where "View all" goes when it has no filters of its own: every place in the card at once,
 * plus the board type or holiday type when all of its links share one. Null when no link
 * filters by anything.
 */
export const combinedSearchUrl = (links, title) => {
  const objects = (Array.isArray(links) ? links : []).filter((l) => l && typeof l === 'object');
  const countries = new Set();
  const destinations = new Set();
  objects.forEach(({ dest }) => {
    if (!dest?.code) return;
    const code = String(dest.code).trim().toUpperCase();
    if (dest.type === 'country') countries.add(code);
    else if (dest.type === 'city') destinations.add(code);
  });
  const shared = (key) => {
    const values = objects.map((l) => l[key]).filter((v) => v != null && v !== '').map(String);
    return values.length && values.length === objects.length && new Set(values).size === 1 ? values[0] : null;
  };
  const board = shared('boardCode');
  const theme = shared('holidayTypeId');

  const qs = new URLSearchParams();
  if (countries.size) qs.set('countries', [...countries].join(','));
  if (destinations.size) qs.set('destinations', [...destinations].join(','));
  if (board) qs.set('boards', board.toUpperCase());
  if (theme) qs.set('themes', theme);
  if ([...qs.keys()].length === 0) return null;
  if (title) qs.set('destinationLabel', title);
  return `/results?${qs.toString()}`;
};

// The drawings themselves are ~1.5 MB, so they load on first use, once, in their own chunk.
let iconData = null;
let iconDataPromise = null;
export const loadTravelIcons = () => {
  if (!iconDataPromise) {
    iconDataPromise = import('../../../../../assets/travel-icons/icons.json')
      .then((mod) => {
        iconData = mod.default || mod;
        return iconData;
      })
      .catch((err) => {
        iconDataPromise = null;
        throw err;
      });
  }
  return iconDataPromise;
};

/** `{ emoji, glyphs }` once the drawings have loaded, null until then (or if they fail to). */
export function useTravelIcons() {
  const [data, setData] = useState(iconData);
  useEffect(() => {
    if (iconData) return undefined;
    let alive = true;
    loadTravelIcons()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { /* the pickers fall back to names */ });
    return () => { alive = false; };
  }, []);
  return data;
}
